/**
 * Rendu du markdown des cahiers des charges, sans dépendance externe.
 *
 * Couvre ce que produit la génération IA et ce qui est réinjecté dans le PDF :
 * titres, listes, cases `- [ ]`, listes numérotées, tableaux, citations,
 * séparateurs, blocs de code, et le style inline gras / italique / code / lien.
 */
const RE_TITRE = /^(#{1,6})\s+(.+)$/;
const RE_SPEC = /^(\s*)[-*+]\s*\[([ xX])\]\s*(.+)$/;
const RE_PUCE = /^(\s*)[-*+]\s+(.+)$/;
const RE_NUM = /^(\s*)(\d+)[.)]\s+(.+)$/;
const RE_IMAGE = /^!\[([^\]]*)\]\((https:\/\/novaforge\.pro\/cdc-assets\/[^)]+)\)$/;
// Pas de lookbehind : certains Safari encore en circulation ne le gèrent pas.
const RE_INLINE = /(\*\*\*.+?\*\*\*|\*\*.+?\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\)|~~.+?~~|\*[^*\n]+\*)/g;

function inline(texte, cle = "i") {
  const morceaux = String(texte ?? "").split(RE_INLINE).filter((m) => m !== undefined && m !== "");
  return morceaux.map((m, i) => {
    const k = `${cle}-${i}`;
    if (m.startsWith("***") && m.endsWith("***"))
      return <strong key={k}><em>{m.slice(3, -3)}</em></strong>;
    if (m.startsWith("**") && m.endsWith("**"))
      return <strong key={k} className="font-semibold text-slate-900">{m.slice(2, -2)}</strong>;
    if (m.startsWith("~~") && m.endsWith("~~"))
      return <span key={k} className="line-through text-slate-400">{m.slice(2, -2)}</span>;
    if (m.startsWith("`") && m.endsWith("`"))
      return <code key={k} className="rounded bg-slate-100 px-1 py-0.5 text-[0.85em] font-mono text-indigo-700">{m.slice(1, -1)}</code>;
    if (m.startsWith("[")) {
      const lien = m.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (lien) return <a key={k} href={lien[2]} target="_blank" rel="noreferrer" className="text-indigo-600 underline">{lien[1]}</a>;
    }
    if (m.startsWith("*") && m.endsWith("*") && m.length > 2)
      return <em key={k}>{m.slice(1, -1)}</em>;
    return <span key={k}>{m}</span>;
  });
}

function Tableau({ lignes, cle }) {
  const grille = lignes
    .map((l) => l.trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim()))
    .filter((cells) => !cells.every((c) => /^:?-{2,}:?$/.test(c)));
  if (!grille.length) return null;
  const [entete, ...corps] = grille;
  return (
    <div className="my-3 overflow-x-auto rounded-lg border border-slate-200">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-indigo-600 text-white">
            {entete.map((c, i) => (
              <th key={i} className="px-3 py-2 text-left text-xs font-semibold">{inline(c, `${cle}-th${i}`)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {corps.map((r, i) => (
            <tr key={i} className={i % 2 ? "bg-slate-50" : "bg-white"}>
              {entete.map((_, j) => (
                <td key={j} className="border-t border-slate-100 px-3 py-2 align-top text-slate-600">
                  {inline(r[j] || "", `${cle}-td${i}${j}`)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function MarkdownView({ source, className = "" }) {
  const lignes = String(source || "").replace(/\r\n/g, "\n").split("\n");
  const blocs = [];
  let paragraphe = [];
  let i = 0;

  const viderParagraphe = () => {
    if (paragraphe.length) {
      blocs.push(
        <p key={`p${blocs.length}`} className="my-2 text-sm leading-relaxed text-slate-600">
          {inline(paragraphe.join(" "), `p${blocs.length}`)}
        </p>
      );
      paragraphe = [];
    }
  };

  while (i < lignes.length) {
    const brute = lignes[i];
    const nu = brute.trim();

    if (!nu) { viderParagraphe(); i += 1; continue; }

    const image = nu.match(RE_IMAGE);
    if (image) {
      viderParagraphe();
      blocs.push(
        <figure key={`img${blocs.length}`} className="my-5 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <img src={image[2]} alt={image[1] || "Maquette fonctionnelle"} loading="lazy" className="block h-auto w-full" />
          {image[1] && <figcaption className="border-t border-slate-100 px-4 py-2 text-center text-xs text-slate-500">{image[1]}</figcaption>}
        </figure>
      );
      i += 1;
      continue;
    }

    if (nu.startsWith("```")) {
      viderParagraphe();
      i += 1;
      const bloc = [];
      while (i < lignes.length && !lignes[i].trim().startsWith("```")) { bloc.push(lignes[i]); i += 1; }
      i += 1;
      blocs.push(
        <pre key={`c${blocs.length}`} className="my-3 overflow-x-auto rounded-lg bg-slate-900 p-3 text-xs text-slate-100">
          <code>{bloc.join("\n")}</code>
        </pre>
      );
      continue;
    }

    if (nu.startsWith("|") && nu.split("|").length > 2) {
      viderParagraphe();
      const bloc = [];
      while (i < lignes.length && lignes[i].trim().startsWith("|")) { bloc.push(lignes[i]); i += 1; }
      blocs.push(<Tableau key={`t${blocs.length}`} lignes={bloc} cle={`t${blocs.length}`} />);
      continue;
    }

    if (/^(-{3,}|\*{3,}|_{3,})$/.test(nu)) {
      viderParagraphe();
      blocs.push(<hr key={`h${blocs.length}`} className="my-4 border-slate-200" />);
      i += 1;
      continue;
    }

    const titre = nu.match(RE_TITRE);
    if (titre) {
      viderParagraphe();
      const niveau = titre[1].length;
      const texte = titre[2];
      const cle = `t${blocs.length}`;
      if (niveau === 1)
        blocs.push(<h1 key={cle} className="mb-1 mt-4 text-xl font-bold text-slate-900">{inline(texte, cle)}</h1>);
      else if (niveau === 2)
        blocs.push(
          <div key={cle} className="mt-5 rounded-lg bg-indigo-600 px-3.5 py-2 text-sm font-semibold text-white">
            {inline(texte, cle)}
          </div>
        );
      else if (niveau === 3)
        blocs.push(
          <h3 key={cle} className="mb-1 mt-4 text-[11px] font-bold uppercase tracking-wider text-indigo-600">
            {inline(texte, cle)}
          </h3>
        );
      else
        blocs.push(<h4 key={cle} className="mb-1 mt-3 text-xs font-semibold text-slate-700">{inline(texte, cle)}</h4>);
      i += 1;
      continue;
    }

    if (nu.startsWith(">")) {
      viderParagraphe();
      blocs.push(
        <blockquote key={`q${blocs.length}`} className="my-2 border-l-2 border-indigo-200 pl-3 text-sm italic text-slate-500">
          {inline(nu.replace(/^>\s?/, ""), `q${blocs.length}`)}
        </blockquote>
      );
      i += 1;
      continue;
    }

    const spec = brute.match(RE_SPEC);
    if (spec) {
      viderParagraphe();
      const profond = spec[1].length >= 2;
      const fait = spec[2].toLowerCase() === "x";
      blocs.push(
        <div key={`s${blocs.length}`} className={`flex items-start gap-2 py-0.5 text-sm ${profond ? "pl-8" : "pl-1"}`}>
          <span className={`mt-[3px] h-3.5 w-3.5 shrink-0 rounded border ${fait ? "border-emerald-500 bg-emerald-500" : "border-slate-300 bg-white"}`}>
            {fait && (
              <svg viewBox="0 0 14 14" className="h-full w-full text-white"><path d="M3 7.5l2.5 2.5L11 4.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
            )}
          </span>
          <span className={fait ? "text-slate-400 line-through" : "text-slate-600"}>
            {inline(spec[3], `s${blocs.length}`)}
          </span>
        </div>
      );
      i += 1;
      continue;
    }

    const num = brute.match(RE_NUM);
    if (num) {
      viderParagraphe();
      blocs.push(
        <div key={`n${blocs.length}`} className={`flex items-start gap-2 py-0.5 text-sm text-slate-600 ${num[1].length >= 2 ? "pl-8" : "pl-1"}`}>
          <span className="mt-px shrink-0 font-semibold text-indigo-500 tabular-nums">{num[2]}.</span>
          <span>{inline(num[3], `n${blocs.length}`)}</span>
        </div>
      );
      i += 1;
      continue;
    }

    const puce = brute.match(RE_PUCE);
    if (puce) {
      viderParagraphe();
      blocs.push(
        <div key={`b${blocs.length}`} className={`flex items-start gap-2 py-0.5 text-sm text-slate-600 ${puce[1].length >= 2 ? "pl-8" : "pl-1"}`}>
          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-400" />
          <span>{inline(puce[2], `b${blocs.length}`)}</span>
        </div>
      );
      i += 1;
      continue;
    }

    paragraphe.push(nu);
    i += 1;
  }
  viderParagraphe();

  return <div className={className}>{blocs}</div>;
}
